import { defineStore } from 'pinia';
import { ref } from 'vue';

import initApi from '@/api/initApiV2';

import {
  AssetCardsApi,
  type AssetCardExpandedDetail,
  type AssetCardExpandedPaginated,
  type AssetCardsApiCancelAssetCardReviewsRequest,
  type AssetCardsApiCreateAssetCardCommentsRequest,
  type AssetCardsApiCreateAssetCardRequest,
  type AssetCardsApiDownloadAssetCardRequest,
  type AssetCardsApiGetAssetCardCommentsRequest,
  type AssetCardsApiGetAssetCardExpandedRequest,
  type AssetCardsApiGetAssetCardLogsRequest,
  type AssetCardsApiGetAssetCardsExpandedRequest,
  type AssetCardsApiRequestAssetCardReviewRequest,
  type AssetCardsApiTakeAssetCardReviewDecisionRequest,
  type AssetCardsApiUpdateAssetCardAssigneeRequest,
  type AssetCardsApiUpdateAssetCardRequest,
  type AssetCardsApiUpdateAssetCardReviewersRequest,
  type AssetCardsApiUpdateAssetCardStatusRequest,
  type Comment,
  type Log,
} from '@/clients/v2';

import { handleApiError } from '@/api/handleApiError.ts';
import type { RequestConfig } from '@/api/Request';
import { AxiosError } from 'axios';

export const useAssetCardStoreV2 = defineStore('asset-card-v2', () => {
  const currentAsset = ref<AssetCardExpandedDetail | null>(null);
  const assetCardsData = ref<AssetCardExpandedPaginated>({
    total: 0,
    items: [],
    page: 0,
    size: 0,
    pages: 0,
  });
  const assetCardComments = ref<Comment[]>([]);
  const assetCardLogs = ref<Log[]>([]);
  const isLoading = ref(false);

  let expandedRequestId = 0;

  const getAssetCardsExpanded = async (
    params: AssetCardsApiGetAssetCardsExpandedRequest,
    options?: RequestConfig,
  ) => {
    const requestId = ++expandedRequestId;
    try {
      isLoading.value = true;
      const assetCardsApi = await initApi(AssetCardsApi);
      const response = await assetCardsApi?.getAssetCardsExpanded(params, options);

      if (!response || requestId !== expandedRequestId) return;

      assetCardsData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    } finally {
      if (requestId === expandedRequestId) isLoading.value = false;
    }
  };

  const getAssetCardExpanded = async (params: AssetCardsApiGetAssetCardExpandedRequest) => {
    try {
      const assetCardsApi = await initApi(AssetCardsApi);
      const response = await assetCardsApi?.getAssetCardExpanded(params);

      if (!response) return;

      currentAsset.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createAssetCard = async (params: AssetCardsApiCreateAssetCardRequest) => {
    try {
      const assetCardsApi = await initApi(AssetCardsApi);
      const response = await assetCardsApi?.createAssetCard(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateAssetCard = async (params: AssetCardsApiUpdateAssetCardRequest) => {
    try {
      const assetCardsApi = await initApi(AssetCardsApi);
      const response = await assetCardsApi?.updateAssetCard(params);

      if (!response) return;

      currentAsset.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateAssetCardAssignee = async (params: AssetCardsApiUpdateAssetCardAssigneeRequest) => {
    try {
      const assetCardsApi = await initApi(AssetCardsApi);
      const response = await assetCardsApi?.updateAssetCardAssignee(params);

      if (!response) return;

      currentAsset.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateAssetCardStatus = async (params: AssetCardsApiUpdateAssetCardStatusRequest) => {
    try {
      const assetCardsApi = await initApi(AssetCardsApi);
      const response = await assetCardsApi?.updateAssetCardStatus(params);

      if (!response) return;

      currentAsset.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const downloadAssetCard = async (params: AssetCardsApiDownloadAssetCardRequest) => {
    try {
      const assetCardsApi = await initApi(AssetCardsApi);
      const response = await assetCardsApi?.downloadAssetCard(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getAssetCardComments = async (params: AssetCardsApiGetAssetCardCommentsRequest) => {
    try {
      const assetCardsApi = await initApi(AssetCardsApi);
      const response = await assetCardsApi?.getAssetCardComments(params);

      if (!response) return;

      assetCardComments.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createAssetCardComments = async (params: AssetCardsApiCreateAssetCardCommentsRequest) => {
    try {
      const assetCardsApi = await initApi(AssetCardsApi);
      const response = await assetCardsApi?.createAssetCardComments(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getAssetCardLogs = async (params: AssetCardsApiGetAssetCardLogsRequest) => {
    try {
      const assetCardsApi = await initApi(AssetCardsApi);
      const response = await assetCardsApi?.getAssetCardLogs(params);

      if (!response) return;

      assetCardLogs.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const requestAssetCardReview = async (params: AssetCardsApiRequestAssetCardReviewRequest) => {
    try {
      const assetCardsApi = await initApi(AssetCardsApi);
      const response = await assetCardsApi?.requestAssetCardReview(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateAssetCardReviewers = async (params: AssetCardsApiUpdateAssetCardReviewersRequest) => {
    try {
      const assetCardsApi = await initApi(AssetCardsApi);
      const response = await assetCardsApi?.updateAssetCardReviewers(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const takeAssetCardReviewDecision = async (
    params: AssetCardsApiTakeAssetCardReviewDecisionRequest,
  ) => {
    try {
      const assetCardsApi = await initApi(AssetCardsApi);
      const response = await assetCardsApi?.takeAssetCardReviewDecision(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const cancelAssetCardReviews = async (params: AssetCardsApiCancelAssetCardReviewsRequest) => {
    try {
      const assetCardsApi = await initApi(AssetCardsApi);
      await assetCardsApi?.cancelAssetCardReviews(params);
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const resetStore = () => {
    currentAsset.value = null;
    assetCardComments.value = [];
    assetCardLogs.value = [];
  };

  return {
    getAssetCardsExpanded,
    getAssetCardExpanded,
    createAssetCard,
    updateAssetCard,
    updateAssetCardAssignee,
    updateAssetCardStatus,
    downloadAssetCard,
    getAssetCardComments,
    createAssetCardComments,
    getAssetCardLogs,
    requestAssetCardReview,
    updateAssetCardReviewers,
    takeAssetCardReviewDecision,
    cancelAssetCardReviews,
    resetStore,

    currentAsset,
    assetCardsData,
    assetCardComments,
    assetCardLogs,
    isLoading,
  };
});
